import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import SortBy from '../../Shared/SortBy';
import ProviderRosterCard from './ProviderRosterCard';
import ProviderRosterTable from './ProviderRosterTable';
import { SORT_OPTIONS, sortProviders } from './sorting';

/**
 * @typedef {import('../types').RosterProvider} RosterProvider
 */

/**
 * Displays list of providers added to the roster.
 * @param {{
 *  providers: RosterProvider[],
 *  onProvidersChange: (updater: (prev: RosterProvider[]) => RosterProvider[]) => void
 *  disabled?: boolean
 * }} props
 */
const ProviderRosterSection = ({
  providers,
  onProvidersChange,
  disabled = false,
}) => {
  const [sortBy, setSortBy] = useState('');

  // Apply sorting to providers for display
  const sortedProviders = useMemo(
    () => sortProviders(providers, sortBy),
    [providers, sortBy],
  );

  const handleProviderChange = useCallback(
    (providerCode, updates) => {
      if (!providerCode || !updates) return;

      onProvidersChange((prevProviders) =>
        prevProviders.map((p) =>
          p.providerCode === providerCode ? { ...p, ...updates } : p,
        ),
      );
    },
    [onProvidersChange],
  );

  // Handle removing a provider from the roster
  const handleRemove = useCallback(
    (providerId) => {
      onProvidersChange((prevProviders) =>
        prevProviders.filter((p) => p.providerCode !== providerId),
      );
    },
    [onProvidersChange],
  );

  return (
    <div className="flex flex-col gap-6 mt-5">
      <div className="flex flex-col gap-2">
        <div className="relative flex justify-between items-center">
          <h2 className="text-2xl font-bold">
            Provider Roster {providers.length > 0 && `(${providers.length})`}
          </h2>
          <SortBy
            options={SORT_OPTIONS}
            value={sortBy}
            onChange={setSortBy}
            className="md:hidden"
          />
        </div>
        <p className="font-thin mb-3">
          A provider will need to confirm that you're able to manage their
          profile before you can make any updates to their profile data.
        </p>
      </div>

      {/* Desktop Table */}
      <ProviderRosterTable
        providers={sortedProviders}
        onProviderChange={handleProviderChange}
        onRemove={handleRemove}
        sortBy={sortBy}
        onSortChange={setSortBy}
        disabled={disabled}
      />

      {/* Mobile Cards */}
      <div
        className="md:hidden flex flex-col gap-6"
        data-testid="provider-roster-cards"
      >
        {sortedProviders.map((provider) => (
          <ProviderRosterCard
            key={provider.providerCode}
            provider={provider}
            onProviderChange={handleProviderChange}
            onRemove={handleRemove}
            disabled={disabled}
            existingProviders={providers}
          />
        ))}
      </div>
    </div>
  );
};

ProviderRosterSection.propTypes = {
  providers: PropTypes.arrayOf(
    PropTypes.shape({
      providerCode: PropTypes.string,
      displayName: PropTypes.string,
      npi: PropTypes.string,
      providerImageUrl: PropTypes.string,
      primarySpecialty: PropTypes.shape({
        specialtyName: PropTypes.string,
        specialtyCode: PropTypes.string,
      }),
      office: PropTypes.shape({
        name: PropTypes.string,
        addressLine: PropTypes.string,
        cityState: PropTypes.string,
        phone: PropTypes.string,
        fax: PropTypes.string,
      }),
      email: PropTypes.string,
      validEmail: PropTypes.bool,
      validLast4: PropTypes.bool,
    }),
  ).isRequired,
  onProvidersChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

export default ProviderRosterSection;
